import React, { useEffect, useState } from "react";
import "./Navbar.scss";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { AppLocation } from "src/redux/main/mainReducer";
import { RootState } from "src/redux/reducers";
import useGetUserAvatarUrl from "src/hooks/useGetUserAvatarUrl";
import {
  closeTrialExceededModal,
  closeTrialLimitationsModal,
  getMembersList,
  setCreatorSearchLockedModalVisibility,
  setStoreConfigurationModalVisibility,
} from "../../redux";
import { uploadAvatarBlob } from "../../utils/uploadFunctions";
import { SkeletonCircle } from "../components/Skeleton/Skeleton";
import CustomImage from "../components/CustomImage/CustomImage";
import TopSearch from "./TopSearch/TopSearch";
import ImageCropModal from "../components/CroppedImageUploader/ImageCropModal/ImageCropModal";
import { TrialLimitationsModal } from "../modals/TrialLimitationsModal/TrialLimitationsModal";
import { TrialExceededModal } from "../modals/TrialExceededModal/TrialExceededModal";
import { RolesInfoModal } from "../modals/ShareModal/RolesInfoModal/RolesInfoModal";
import { WorkspaceRolesInfoModal } from "../modals/ShareModal/WorkspaceRolesInfoModal/WorkspaceRolesInfoModal";
import { SettingsModal } from "../SettingsModal/SettingsModal";
import { SettingsContext, SettingsTab } from "../SettingsModal/enums";
import SettingsTrialDropdown from "../dropdowns/SettingsTrialDropdown/SettingsTrialDropdown";
import { openSettingsModal } from "../SettingsModal/methods";
import SubscriptionUsageModal, {
  SubscriptionUsageModalMode,
} from "../subscription/modals/SubscriptionUsageModal/SubscriptionUsageModal";
import SubscriptionModals from "../subscription/components/SubscriptionModals/SubscriptionModals";
import IDHFormattedMessage from "../components/IDHFormattedMessage/IDHFormattedMessage";
import { DEMO_MODE_INFO_BAR_PADDING } from "../components/DemoModeInfoBar/DemoModeInfoBar";
import StoreConfigurationModal from "../modals/StoreConfigurationModal/StoreConfigurationModal";
import FeatureLockedModal from "../modals/FeatureLockedModal/FeatureLockedModal";
import { Button } from "src/app/components/Button/Button";
import { openAIChat } from "src/redux/ai-chat/aiChatActions";
import { ReactComponent as AIStarsIcon } from "src/images/ai-stars.svg";

const shouldShowTopSearch = (pathName: string) => {
  const blacklist = ["creator-discovery-tool"];
  return !blacklist.some((item) => pathName.includes(item));
};

function Navbar() {
  const [loading, setLoading] = useState(false);
  const [showImageCropModal, setShowImageCropModal] = useState(false);
  const [showRolesInfoModal, setShowRolesInfoModal] = useState(false);
  const [showWorkspaceRolesInfoModal, setShowWorkspaceRolesInfoModal] =
    useState(false);

  const avatarUrl = useGetUserAvatarUrl();

  const {
    globalModalsReducer: {
      trialExceededModalVisible,
      trialLimitationsModalVisible,
      storeConfigurationModalVisible,
      creatorSearchLockedModalVisible,
    },
    mainReducer: {
      activeWorkspaceUuid,
      settingsModalData,
      identity,
      notifications,
    },
    projectReducer: { membersList },
    audiencesManagerReducer: { trialExceededCDTModalVisible },
  } = useSelector((state: RootState) => state);

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const onChangeAvatar = async (file: any) => {
    if (identity?.id) {
      setLoading(true);
      await uploadAvatarBlob(file, dispatch, identity?.id, activeWorkspaceUuid);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeWorkspaceUuid && identity?.id && !membersList?.length) {
      dispatch(getMembersList(activeWorkspaceUuid));
    }
  }, [activeWorkspaceUuid, dispatch, identity.id, membersList.length]);

  const handleOpenSettingsModal = (
    context: SettingsContext,
    tab: SettingsTab,
  ) => {
    openSettingsModal(history, AppLocation.Navbar, context, tab);
  };

  const showOnboarding = identity?.errors?.includes("onboarding");

  const trialLimitationConditions =
    trialLimitationsModalVisible &&
    trialExceededModalVisible === false &&
    trialExceededCDTModalVisible === false;

  const renderTrialLimitationsModal =
    trialLimitationConditions && identity?.trialLimits;

  const renderSubscriptionUsageModal =
    trialLimitationConditions && identity?.subscriptionData && !showOnboarding;

  const calculatePaddingToOffsetNotifications = () => {
    return notifications.length * 44;
  };

  const getPaddingToOffsetDemoModeInfoBar = () => {
    return identity?.isDemoWorkspace ? DEMO_MODE_INFO_BAR_PADDING : 0;
  };

  return (
    <header
      className="navbar"
      style={{
        marginTop:
          calculatePaddingToOffsetNotifications() +
          getPaddingToOffsetDemoModeInfoBar(),
      }}
    >
      <div className="navbar__left">
        {shouldShowTopSearch(location.pathname) && <TopSearch />}
      </div>
      <div className="navbar__right">
        <Button
          variant="white-with-light-grey-border"
          size="small"
          textWithIcon
          onClick={() => dispatch(openAIChat())}
        >
          <AIStarsIcon />
          <IDHFormattedMessage
            id="ws_ai_assistant"
            defaultMessage="AI Assistant"
          />
        </Button>

        {loading ? (
          <SkeletonCircle size={32} marginRight={12} />
        ) : (
          <div className="navbar__right-avatar">
            <CustomImage
              src={`${avatarUrl}`}
              alt="avatar"
              onClick={() => setShowImageCropModal(true)}
            />
          </div>
        )}
        <div className="navbar__right-user-data">
          <div className="navbar__right-name">{identity?.name}</div>
          <div className="navbar__right-workspace-name">
            {identity?.wsWorkspace?.name}
          </div>
        </div>
        <SettingsTrialDropdown />
      </div>
      {settingsModalData?.location === AppLocation.Navbar && (
        <SettingsModal
          members={{
            openShareModal: () =>
              openSettingsModal(
                history,
                AppLocation.Navbar,
                SettingsContext.Project,
                SettingsTab.Members,
              ),
            openRolesInfoModal: () => setShowRolesInfoModal(true),
            openWorkspaceRolesInfoModal: () =>
              setShowWorkspaceRolesInfoModal(true),
          }}
          contextTargetUuid={activeWorkspaceUuid || ""}
          disableTransition
        />
      )}
      {showRolesInfoModal && (
        <RolesInfoModal
          onClose={() => setShowRolesInfoModal(false)}
          openShareModal={() =>
            handleOpenSettingsModal(
              SettingsContext.Workspace,
              SettingsTab.Members,
            )
          }
        />
      )}
      {showWorkspaceRolesInfoModal && (
        <WorkspaceRolesInfoModal
          onClose={() => setShowWorkspaceRolesInfoModal(false)}
        />
      )}
      {showImageCropModal && (
        <ImageCropModal
          uploadFunction={onChangeAvatar}
          onClose={() => setShowImageCropModal(false)}
          circle
        />
      )}
      {trialExceededModalVisible && (
        <TrialExceededModal
          onClose={() => dispatch(closeTrialExceededModal())}
        />
      )}
      {renderTrialLimitationsModal && (
        <TrialLimitationsModal
          onClose={() => dispatch(closeTrialLimitationsModal())}
        />
      )}
      {renderSubscriptionUsageModal && (
        <SubscriptionUsageModal
          mode={SubscriptionUsageModalMode.Info}
          onClose={() => dispatch(closeTrialLimitationsModal())}
        />
      )}
      {storeConfigurationModalVisible && (
        <StoreConfigurationModal
          onClose={() => dispatch(setStoreConfigurationModalVisibility(false))}
        />
      )}

      {/* Creator Search Locked Modal */}
      {creatorSearchLockedModalVisible && (
        <FeatureLockedModal
          onClose={() => dispatch(setCreatorSearchLockedModalVisibility(false))}
          text={
            <IDHFormattedMessage
              id="ws_creator_search_is_not_available_in_demo_mode"
              defaultMessage="Creator Search is not available in demo mode."
            />
          }
        />
      )}

      <SubscriptionModals />
    </header>
  );
}

export default Navbar;
